import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import { formatDateWithFallback } from "@/core/utils/func/formatDateWithFallback";
import type { NotificationLogDto } from "@/features/notification/api/dto";
import { NotificationStatusBadge } from "@/features/notification/components/logs/notification-status-badge";

/**
 * Colonnes de l'historique d'envoi (`POST /NotificationLogs/all`) — les
 * filtres statut/canal/partyId sont pilotés par la barre externe
 * `NotificationLogsFilters` (mode server) : les champs DataTableClar sont
 * masqués (`denied`) pour ne pas simuler un filtre non câblé.
 * Identifiants et références en `NoTranslate` (règle notranslate).
 */
export const notificationLogsColumns: ColumnsClarFactory<NotificationLogDto> =
  () => [
    {
      id: "templateCode",
      accessorKey: "templateCode",
      header: t("notifications.logs.columns.templateCode"),
      cell: ({ getValue }) => (
        <NoTranslate as="span" className="font-mono text-xs">
          {(getValue() as string) || "—"}
        </NoTranslate>
      ),
      meta: { type: "denied" },
    },
    {
      id: "channel",
      accessorKey: "channel",
      header: t("notifications.logs.columns.channel"),
      cell: ({ getValue }) => (
        <NoTranslate as="span">
          <Badge variant="secondary">{getValue() as string}</Badge>
        </NoTranslate>
      ),
      meta: { type: "denied" },
    },
    {
      id: "partyId",
      accessorKey: "partyId",
      header: t("notifications.logs.columns.partyId"),
      cell: ({ getValue }) => {
        const id = getValue() as string | undefined | null;
        return (
          <NoTranslate as="span" className="font-mono text-xs">
            {id ? `${id.slice(0, 8)}…` : "—"}
          </NoTranslate>
        );
      },
      meta: { type: "denied" },
    },
    {
      id: "status",
      accessorKey: "status",
      header: t("notifications.logs.columns.status"),
      cell: ({ row }) => <NotificationStatusBadge status={row.original.status} />,
      meta: { type: "denied" },
    },
    {
      id: "providerMessageId",
      accessorKey: "providerMessageId",
      header: t("notifications.logs.columns.providerMessageId"),
      cell: ({ getValue }) => (
        <NoTranslate as="span" className="font-mono text-xs">
          {(getValue() as string) || "—"}
        </NoTranslate>
      ),
      meta: { type: "denied" },
    },
    {
      id: "errorMessage",
      accessorKey: "errorMessage",
      header: t("notifications.logs.columns.errorMessage"),
      cell: ({ getValue }) => {
        const value = getValue() as string | null | undefined;
        return value ? (
          <span className="line-clamp-1 text-destructive">{value}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        );
      },
      meta: { type: "denied" },
    },
    {
      id: "sentAt",
      accessorKey: "sentAt",
      header: t("notifications.logs.columns.sentAt"),
      cell: ({ getValue }) => (
        <NoTranslate as="span">
          {formatDateWithFallback(getValue() as string | null, "—")}
        </NoTranslate>
      ),
      meta: { type: "denied" },
    },
    {
      id: "createdAt",
      accessorKey: "createdAt",
      header: t("notifications.logs.columns.createdAt"),
      cell: ({ getValue }) => (
        <NoTranslate as="span">
          {formatDateWithFallback(getValue() as string | null, "—")}
        </NoTranslate>
      ),
      meta: { type: "denied" },
    },
  ];
